import { useCallback, useState } from "react";

import useLayoutStore from "components/Layout/stores/useLayoutStore";
import { MainNavigationDropdownMenuItemsCustomRendererProps } from "components/MainNavigation/components/atoms/DropdownMenu/Items";
import MainNavigationGroupItem from "components/MainNavigation/components/elements/GroupItem";
import { MainNavigationSecondaryMenuItemKeys } from "components/MainNavigation/config/menuItemsSecondary";
import useMainNavigationFadeTransition from "components/MainNavigation/hooks/useFadeTransition";
import { selectNavigationShowsExpandedContent } from "components/MainNavigation/selectors/navigationState";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import { MainNavigationGroupItem as MainNavigationGroupItemType } from "components/MainNavigation/types";
import Link from "ds/components/Link";
import TooltipModal from "ds/components/TooltipModal";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useInitFeaturebase from "hooks/useInitFeaturebase";

import MainNavigationProductUpdatesMenuItem from "./ProductUpdatesMenuItem";

type MainNavigationSupportProps = {
  item: MainNavigationGroupItemType;
};

const MainNavigationSupport = ({ item }: MainNavigationSupportProps) => {
  const showsExpandedContent = useMainNavigationStore(selectNavigationShowsExpandedContent);
  const isFullScreenModalOpen = useLayoutStore((state) => state.isFullScreenModalOpen);
  const { transitionClasses } = useMainNavigationFadeTransition(showsExpandedContent);

  const [showFeaturebaseTooltip, setShowFeaturebaseTooltip] = useState(false);

  useInitFeaturebase(setShowFeaturebaseTooltip);

  const handleFeatureBaseClick = useCallback(() => {
    setShowFeaturebaseTooltip(false);
    setTimeout(() => {
      if (window.Featurebase) {
        window.Featurebase("manually_open_changelog_popup");
      }
    }, 100);
  }, []);

  const handleTooltipClose = useCallback(() => {
    setShowFeaturebaseTooltip(false);
    if (window.Featurebase) {
      window.Featurebase("set_all_changelogs_as_viewed");
    }
  }, []);

  const menuItemsCustomRenderers = {
    [MainNavigationSecondaryMenuItemKeys.ProductUpdates]: ({
      item,
      closeDropdown,
    }: MainNavigationDropdownMenuItemsCustomRendererProps) => (
      <MainNavigationProductUpdatesMenuItem
        item={item}
        onCloseDropdown={closeDropdown}
        withNewBadge={showFeaturebaseTooltip}
      />
    ),
  };

  return (
    <TooltipModal
      placement="right"
      forceOpen={showFeaturebaseTooltip && !isFullScreenModalOpen}
      closeCallback={handleTooltipClose}
      on={({ ref }) => (
        <MainNavigationGroupItem
          tooltipModalRef={ref}
          group={item}
          dropdownOnly
          dropdownMenuItemsCustomRenderers={menuItemsCustomRenderers}
          transitionClasses={transitionClasses}
        />
      )}
    >
      <TooltipModalTitle>Spacelift has updated! 🚀</TooltipModalTitle>
      <TooltipModalBody align="start">
        Explore the latest features and changes.
        <Link size="small" onPress={handleFeatureBaseClick}>
          See what's new
        </Link>
      </TooltipModalBody>
    </TooltipModal>
  );
};

export default MainNavigationSupport;
